// BulkMutateToolUI — branded tool-UI cards for the agent's bulk-mutate tools,
// rendered inline in the Aidea Assistant chat thread. One shared <BulkMutateCard>
// body handles all three ops (keyed by `op`); three thin makeAssistantToolUI
// registrations bind it to the three tool names (assistant-ui registers per tool
// name, so we need one registration each — see the plan's option (a)).
//
// The backend returns a `mutationIntent` envelope (sibling of the popup gateway's
// openPopup envelope):
//   { applyMutation, op, tier, params, scope, cost?, summary, error? }
//
// Behaviour by tier:
//   - INSTANT (setSceneFontAll, setNarrationVoiceAll): on mount, if
//     shouldAutoApplyOnce(toolCallId), call the executor immediately (free +
//     redux-undo reversible). Render a "✓ {summary}" line + an Undo button. The
//     voice card also offers a "Re-generate audio" stub (re-gen fan-out deferred).
//   - GATED (animateAllImagesInScene): render {summary} + cost.label + Apply /
//     Cancel. On Apply call runAnimateAllImages (paid fan-out), then show a
//     progress view driven by useSessionImageJobs filtered to the returned
//     jobIds. NEVER auto-applies.
//
// Redux access: ChatPanel mounts under EditorApp, which is wrapped in
// react-redux <Provider> (ReduxProvider.jsx), so useDispatch()/useStore() resolve
// the editor store. Confirmed by reading ReduxProvider.jsx + EditorApp.jsx.
//
// Styling follows the chat scoping contract (tw- prefix, only under
// .aidea-chat-root, brand tokens via CSS vars, no emoji — except the ✓/✕ glyphs
// already used elsewhere are avoided in favour of Lucide icons).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, AlertIcon, CheckIcon, FileTextIcon, SoundIcon, ClapperIcon } from "../icons";
import { MUTATION_REGISTRY, shouldAutoApplyOnce } from "../aiMutationGateway";
import { runAnimateAllImages } from "../runAnimateAllImages";
import { runEditMotionScene, applyMotionEdit } from "../runEditMotionScene";
import useSessionImageJobs from "../useSessionImageJobs";
import {
  DENSITIES,
  GRAPHICS,
  ICON_MODES,
  TEXT_MODES,
  initialChoice,
  motionCardCopy,
  motionResume,
  startMotionDesign,
} from "../runMotionDirector";
import { MotionChoices } from "./MotionChoices";
import { runRedesignScene } from "../runRedesignScene";
import { useChatCommit } from "../ChatCommitContext";
import { applyMotionPlan } from "../../../utils/applyMotionPlan";
import { planTextMeasure } from "../../../utils/motionImporter";
import { approveJob } from "../../../../common/server_utils.es6";
import { waitForJob } from "../../../../common/job_poll.es6";
import { onJobsSettled, getSettledOutcome, SETTLED_APPROVED } from "../../../utils/jobSettled";
import i18n from "../../../../locales/i18n";

// Per-op presentation (chip label + header icon). Keyed by the envelope `op`.
const OP_META = {
  setSceneFontAll: { label: "Font", Icon: FileTextIcon },
  setNarrationVoiceAll: { label: "Voice", Icon: SoundIcon },
  animateAllImagesInScene: { label: "Animate", Icon: ClapperIcon },
  motionDirectorScene: { label: "Motion", labelKey: "aideaChat.motion.chip", Icon: ClapperIcon },
  redesignScene: { label: "Redesign", labelKey: "aideaChat.redesign.chip", Icon: ClapperIcon },
  editMotionScene: {
    label: "Edit",
    labelKey: "aideaChat.motionEdit.chip",
    Icon: ClapperIcon,
  },
};

// Parse the tool result into the mutation envelope (or { error } / null). The
// backend sends a JSON string; some transports may hand it back parsed. Defensive
// on both axes; never throws. Mirrors GenerateImageToolUI.parseImageResult.
function parseMutationResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  if (typeof data.error === "string" && data.error.trim()) {
    return { error: data.error.trim() };
  }
  if (data.applyMutation !== true || typeof data.op !== "string") return null;
  return {
    applyMutation: true,
    op: data.op,
    tier: data.tier === "gated" ? "gated" : "instant",
    params: data.params && typeof data.params === "object" ? data.params : {},
    scope: data.scope && typeof data.scope === "object" ? data.scope : {},
    cost: data.cost && typeof data.cost === "object" ? data.cost : null,
    summary: typeof data.summary === "string" ? data.summary : "",
  };
}

function HeaderChip({ op }) {
  const meta = OP_META[op] || { label: "Mutate", Icon: FileTextIcon };
  const Icon = meta.Icon;
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <Icon size={12} />
      {meta.labelKey ? i18n.t(meta.labelKey) : meta.label}
    </span>
  );
}

function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[440px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

function RunningState({ op }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip op={op} />
      </div>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          Preparing the change…
        </span>
      </div>
    </Card>
  );
}

function ErrorState({ op, message }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip op={op} />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">{message || "Couldn't apply the change."}</span>
      </div>
    </Card>
  );
}

// Small action button used by Undo / Apply / Cancel / Re-generate, styled to the
// chat brand tokens. `variant`: "primary" (filled) | "ghost" (outlined).
function ActionButton({ label, onClick, disabled, variant = "ghost", Icon }) {
  const base =
    "tw-mt-2 tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-py-1.5 tw-text-[12.5px] tw-font-medium tw-transition-colors tw-duration-200 disabled:tw-cursor-not-allowed disabled:tw-opacity-50";
  const tone =
    variant === "primary"
      ? "tw-bg-[var(--ac-ink)] tw-text-[var(--aidea-on-primary)] hover:tw-opacity-90 tw-cursor-pointer"
      : "tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-text-[var(--aidea-fg)] hover:tw-bg-[var(--aidea-muted-bg)] tw-cursor-pointer";
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${tone}`}>
      {Icon ? <Icon size={13} /> : null}
      {label}
    </button>
  );
}

// INSTANT card body: auto-applies once on mount, then shows a confirmation +
// Undo. The voice op also renders a (stubbed) re-generate offer.
function InstantState({ op, params, scope, summary, toolCallId }) {
  const dispatch = useDispatch();
  const store = useStore();
  const appliedRef = useRef(false);
  const [regenNote, setRegenNote] = useState("");

  useEffect(() => {
    if (appliedRef.current) return;
    appliedRef.current = true;
    const key = toolCallId || `${op}:${JSON.stringify(params)}`;
    if (!shouldAutoApplyOnce(key)) return;
    const entry = MUTATION_REGISTRY[op];
    if (entry && typeof entry.executor === "function") {
      entry.executor(dispatch, store.getState, params, scope);
    }
  }, [op, params, scope, toolCallId, dispatch, store]);

  const onUndo = useCallback(() => {
    dispatch(UndoActionCreators.undo());
  }, [dispatch]);

  // Voice re-gen fan-out is deferred (the instant set + the offer is the firm
  // slice-1 requirement). Make it a clearly-labelled, non-destructive stub: it
  // fires NO TTS, just notes that re-gen is coming.
  const onRegen = useCallback(() => {
    setRegenNote(
      "Re-generating existing audio in the new voice is coming soon — the new voice is already set for future narration."
    );
  }, []);

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip op={op} />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
        <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
          <CheckIcon size={16} />
        </span>
        <div className="tw-min-w-0">
          <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
            {summary || "Applied the change."}
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <ActionButton label="Undo" onClick={onUndo} />
            {op === "setNarrationVoiceAll" ? (
              <ActionButton label="Re-generate audio" onClick={onRegen} />
            ) : null}
          </div>
          {regenNote ? (
            <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-italic tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
              {regenNote}
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

// Progress view shown after a gated Apply. Filters the session image-jobs stream
// to the returned jobIds (best-effort match) and shows a simple count.
function AnimateProgress({ jobIds, errors }) {
  const { items } = useSessionImageJobs();
  const idSet = useMemo(() => new Set(jobIds), [jobIds]);
  const completed = useMemo(
    () => items.filter((it) => idSet.has(it.jobId) && it.status === "completed").length,
    [items, idSet]
  );
  const total = jobIds.length;

  return (
    <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
      <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
        {completed >= total && total > 0 ? (
          <CheckIcon size={16} />
        ) : (
          <SpinnerIcon size={16} className="aidea-spin" />
        )}
      </span>
      <div className="tw-min-w-0">
        <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
          {total > 0
            ? `Animating images — ${completed} of ${total} done`
            : "No animation jobs started."}
        </p>
        {errors.length ? (
          <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
            {errors.length} image{errors.length === 1 ? "" : "s"} couldn't be queued.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// One edit to an existing motion scene: confirm → run → show the moment before
// and after. Separate from GatedState because the shape of the work is
// different — a single job whose RESULT matters (a new document url), not a
// fan-out whose progress matters.
//
// Never auto-applies. A restored conversation remounts every card, and
// re-running this one would apply the same edit a second time on top of the
// first.
function MotionEditState({ op, params, scope, summary }) {
  const store = useStore();
  const dispatch = useDispatch();
  const [phase, setPhase] = useState("confirm"); // confirm | running | done | failed | cancelled
  const [error, setError] = useState(null);
  const [shots, setShots] = useState(null); // { beforePng, afterPng }

  const onApply = useCallback(async () => {
    setPhase("running");
    setError(null);
    try {
      const started = await runEditMotionScene(dispatch, store.getState, params, scope);
      if (started.error || !started.jobId) {
        setError(started.error || "Could not start the edit.");
        setPhase("failed");
        return;
      }
      const result = await waitForJob(started.jobId);
      if (!result || !result.htmlUrl) {
        setError("The edit finished without producing a scene.");
        setPhase("failed");
        return;
      }
      // Point the box at the new document. The old one stays in storage, so
      // an edit the author dislikes costs one undo.
      applyMotionEdit(dispatch, store.getState, started.boxId, result);
      setShots({ beforePng: result.beforePng, afterPng: result.afterPng });
      setPhase("done");
    } catch (err) {
      setError((err && err.message) || String(err));
      setPhase("failed");
    }
  }, [dispatch, store, params, scope]);

  const onCancel = useCallback(() => setPhase("cancelled"), []);

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip op={op} />
      </div>
      {phase === "confirm" ? (
        <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
          <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
            <ClapperIcon size={16} />
          </span>
          <div className="tw-min-w-0">
            <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
              {summary || "Edit this motion scene."}
            </p>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-2">
              <ActionButton label="Apply" onClick={onApply} variant="primary" />
              <ActionButton label="Cancel" onClick={onCancel} />
            </div>
          </div>
        </div>
      ) : phase === "running" ? (
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-3">
          <SpinnerIcon size={16} className="aidea-spin" />
          <p className="tw-m-0 tw-text-[13px] tw-text-[var(--aidea-fg)]">
            {i18n.t("aideaChat.motionEdit.running") || "Sahne düzenleniyor..."}
          </p>
        </div>
      ) : phase === "cancelled" ? (
        <div className="tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          {i18n.t("aideaChat.motionEdit.cancelled") || "İptal edildi — sahne değişmedi."}
        </div>
      ) : phase === "failed" ? (
        <div className="tw-px-3 tw-py-3">
          <p className="tw-m-0 tw-text-[13px] tw-text-[var(--aidea-fg)]">
            {i18n.t("aideaChat.motionEdit.failed") ||
              "Düzenleme yapılamadı — sahne olduğu gibi kaldı."}
          </p>
          {error ? (
            <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
              {error}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="tw-px-3 tw-py-3">
          <p className="tw-m-0 tw-mb-2 tw-text-[13px] tw-text-[var(--aidea-fg)]">
            {i18n.t("aideaChat.motionEdit.done") || "Hazır. Aynı an, öncesi ve sonrası:"}
          </p>
          {shots && shots.beforePng && shots.afterPng ? (
            <div className="tw-flex tw-gap-2">
              {[shots.beforePng, shots.afterPng].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={i === 0 ? "before" : "after"}
                  className="tw-w-1/2 tw-rounded-md tw-border tw-border-[var(--aidea-border)]"
                />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

// GATED card body: confirm (scope + cost) → Apply fans out, then progress. Never
// auto-applies.
function GatedState({ op, params, scope, cost, summary }) {
  const store = useStore();
  const dispatch = useDispatch();
  const [phase, setPhase] = useState("confirm"); // confirm | running | done
  const [jobIds, setJobIds] = useState([]);
  const [errors, setErrors] = useState([]);

  const onApply = useCallback(async () => {
    setPhase("running");
    try {
      const { jobs, errors: errs } = await runAnimateAllImages(
        dispatch,
        store.getState,
        params,
        scope
      );
      setJobIds(jobs.map((j) => j.jobId));
      setErrors(errs || []);
    } catch (err) {
      setErrors([{ boxId: null, error: (err && err.message) || String(err) }]);
    }
    setPhase("done");
  }, [dispatch, store, params, scope]);

  const onCancel = useCallback(() => setPhase("cancelled"), []);

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip op={op} />
      </div>
      {phase === "confirm" ? (
        <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
          <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
            <ClapperIcon size={16} />
          </span>
          <div className="tw-min-w-0">
            <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
              {summary || "Animate all images in this scene."}
            </p>
            {cost && cost.label ? (
              <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
                {cost.label}
              </p>
            ) : null}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <ActionButton label="Apply" onClick={onApply} variant="primary" />
              <ActionButton label="Cancel" onClick={onCancel} />
            </div>
          </div>
        </div>
      ) : phase === "cancelled" ? (
        <div className="tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          Cancelled — nothing was animated.
        </div>
      ) : (
        <AnimateProgress jobIds={jobIds} errors={errors} />
      )}
    </Card>
  );
}

// Error codes (runMotionDirector, waitForJob rejections, "no-plan") → friendly copy.
const MOTION_ERROR_KEYS = {
  "scene-missing": "aideaChat.motion.sceneMissing",
  "no-images": "aideaChat.motion.noImages",
  "no-training": "aideaChat.motion.noTraining",
  "insufficient-balance": "aideaChat.motion.insufficientBalance",
  timeout: "aideaChat.motion.timeout",
};

// One poll per job across remounts: a remounted card shares the in-flight promise.
const inFlightPolls = new Map();
function pollMotionJob(jobId) {
  if (!inFlightPolls.has(jobId)) {
    const p = waitForJob(jobId).finally(() => inFlightPolls.delete(jobId));
    inFlightPolls.set(jobId, p);
  }
  return inFlightPolls.get(jobId);
}

// MOTION DIRECTOR card: confirm (cost) → Design (paid job) → poll → "Apply to
// scene" → one-step Undo. Its lifecycle lives on its tool-call entry in the chat
// thread (getToolState/setToolState), so a card that remounts (chat reopened,
// editor reloaded) resumes the same job — a reload mid-POST re-sends the same
// idempotencyKey, which mw maps to the same job — and stays applied once applied.
// Apply always targets scope.navId — the scene the user asked about.
function MotionDirectorState({ op, params, scope, cost, toolCallId }) {
  const store = useStore();
  const dispatch = useDispatch();
  const navId = scope && scope.navId;
  const key = toolCallId || `${op}:${navId}`;
  const { getToolState = () => null, setToolState = () => {} } = useChatCommit();
  const save = useCallback((entry) => setToolState(key, entry), [setToolState, key]);
  const [entry] = useState(() => getToolState(key));
  // confirm | running | ready | applied | error | cancelled
  const [phase, setPhase] = useState(() => {
    const resume = motionResume(entry);
    return resume === "applied" ? "applied" : resume === "confirm" ? "confirm" : "running";
  });
  const [plan, setPlan] = useState(null);
  const [imageSwaps, setImageSwaps] = useState([]);
  const [keptImages, setKeptImages] = useState(0);
  const [choice, setChoice] = useState(() => ({
    density: initialChoice(params, "density", DENSITIES, "key"),
    graphics: initialChoice(params, "graphics", GRAPHICS, "balanced"),
    text: initialChoice(params, "text", TEXT_MODES, "auto"),
    icons: initialChoice(params, "icons", ICON_MODES, "auto"),
  }));
  const [adjustments, setAdjustments] = useState(0);
  const [created, setCreated] = useState(() => (entry && entry.applied) || 0);
  const [error, setError] = useState("");
  // Undo only when applied in THIS mount: redux-undo history does not survive a reload.
  const [canUndo, setCanUndo] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fail = useCallback((code) => {
    if (!mounted.current) return;
    setError(i18n.t(MOTION_ERROR_KEYS[code] || "aideaChat.motion.failed"));
    setPhase("error");
  }, []);

  // Uygulanan planın job'u — apply'da approve edilir ki widget'ta "Onay Bekliyor"
  // kartı kalmasın (plan sohbet kartından uygulanıyor, widget'tan değil).
  const motionJobIdRef = useRef(null);

  const poll = useCallback(
    async (jobId) => {
      motionJobIdRef.current = jobId;
      setPhase("running");
      try {
        const result = await pollMotionJob(jobId);
        if (!result || !result.plan || !Array.isArray(result.plan.elements))
          throw new Error("no-plan");
        if (!mounted.current) return;
        setPlan(result.plan);
        setImageSwaps(Array.isArray(result.imageSwaps) ? result.imageSwaps : []);
        setKeptImages(Number(result.keptImages) || 0);
        setAdjustments(Number(result.adjustments) || 0);
        setPhase("ready");
      } catch (err) {
        fail((err && err.message) || "");
      }
    },
    [fail]
  );

  const design = useCallback(
    async (choice) => {
      setPhase("running");
      const out = await startMotionDesign(key, save, dispatch, store.getState, choice, scope);
      if (!out.jobId) {
        fail(out.error);
        return;
      }
      poll(out.jobId);
    },
    [dispatch, store, scope, key, save, poll, fail]
  );
  const onDesign = useCallback(() => design(choice), [design, choice]);

  // İş tepsisinde kapanan iş: onay planı sahneye zaten kurdu (JobWidgetHost),
  // ret sonucu istemiyor — kart "Apply"ı yeniden sunmamalı. Sohbet o sırada
  // kapalıysa kart remount'ta kalıcı defterden (getSettledOutcome) öğrenir.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const settleCard = useCallback((outcome) => {
    if (!mounted.current) return;
    setCanUndo(false);
    setPhase(outcome === SETTLED_APPROVED ? "trayApplied" : "trayDeclined");
  }, []);
  useEffect(
    () =>
      onJobsSettled((jobIds, outcomes) => {
        const jobId = motionJobIdRef.current || getToolState(key)?.jobId;
        if (!jobId || !jobIds.includes(jobId)) return;
        // Kartın kendi Apply'ı da işi onaylar → zaten "applied", dokunma.
        if (["applied", "trayApplied", "trayDeclined", "cancelled"].includes(phaseRef.current))
          return;
        settleCard(outcomes[jobId]);
      }),
    [key, getToolState, settleCard]
  );

  // Resume once on mount from the thread entry: poll its job, or — Design clicked, POST
  // unanswered (still in flight, or the editor reloaded) — re-send it (same key).
  useEffect(() => {
    const resume = motionResume(entry);
    const settled = resume === "poll" ? getSettledOutcome(entry.jobId) : null;
    if (settled) {
      motionJobIdRef.current = entry.jobId;
      settleCard(settled);
    } else if (resume === "poll") poll(entry.jobId);
    else if (resume === "repost") design(choice);
  }, [key]); // ponytail: mount-only; params/scope are new objects every render

  const onApply = useCallback(async () => {
    const measure = await planTextMeasure(plan); // loads fonts, then text is fitted to its box
    const out = applyMotionPlan(dispatch, store.getState, plan, {
      navId,
      measure,
      imageSwaps,
    });
    if (out.error) {
      fail(out.error);
      return;
    }
    const motionJobId = motionJobIdRef.current || getToolState(key)?.jobId;
    if (motionJobId) approveJob(motionJobId);
    save({ ...getToolState(key), applied: out.created });
    setCreated(out.created);
    setCanUndo(true);
    setPhase("applied");
  }, [dispatch, store, plan, imageSwaps, navId, key, save, getToolState, fail]);

  const onUndo = useCallback(() => dispatch(UndoActionCreators.undo()), [dispatch]);
  const count = plan ? plan.elements.length : 0;
  const copy = motionCardCopy(params, cost, (k, o) => i18n.t(k, o));
  const line = (text) => (
    <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">{text}</p>
  );

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip op={op} />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
        <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
          {phase === "running" ? (
            <SpinnerIcon size={16} className="aidea-spin" />
          ) : phase === "error" ? (
            <AlertIcon size={16} />
          ) : phase === "applied" || phase === "trayApplied" ? (
            <CheckIcon size={16} />
          ) : (
            <ClapperIcon size={16} />
          )}
        </span>
        <div className="tw-min-w-0">
          {phase === "confirm" ? (
            <>
              {line(copy.summary)}
              {copy.cost ? (
                <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
                  {copy.cost}
                </p>
              ) : null}
              <MotionChoices value={choice} onChange={setChoice} />
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <ActionButton
                  label={i18n.t("aideaChat.motion.design")}
                  onClick={onDesign}
                  variant="primary"
                />
                <ActionButton
                  label={i18n.t("aideaChat.motion.cancel")}
                  onClick={() => setPhase("cancelled")}
                />
              </div>
            </>
          ) : phase === "running" ? (
            line(i18n.t("aideaChat.motion.designing"))
          ) : phase === "ready" ? (
            <>
              {line(i18n.t("aideaChat.motion.ready", { count }))}
              {keptImages > 0 ? (
                <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
                  {i18n.t("aideaChat.motion.someKept")}
                </p>
              ) : null}
              <ActionButton
                label={i18n.t("aideaChat.motion.apply")}
                onClick={onApply}
                variant="primary"
              />
            </>
          ) : phase === "applied" ? (
            <>
              {line(i18n.t("aideaChat.motion.applied", { count: created }))}
              {adjustments > 0 ? (
                <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
                  {i18n.t("aideaChat.motion.adjusted", { count: adjustments })}
                </p>
              ) : null}
              {canUndo ? (
                <ActionButton label={i18n.t("aideaChat.motion.undo")} onClick={onUndo} />
              ) : null}
            </>
          ) : phase === "trayApplied" ? (
            line(i18n.t("aideaChat.motion.appliedFromTray"))
          ) : phase === "trayDeclined" ? (
            line(i18n.t("aideaChat.motion.declinedFromTray"))
          ) : phase === "cancelled" ? (
            line(i18n.t("aideaChat.motion.cancelled"))
          ) : (
            line(error)
          )}
        </div>
      </div>
    </Card>
  );
}

// REDESIGN card: confirm (format + cost) → Redesign (paid server-side rebuild; runRedesignScene
// waits for it and re-imports the training) → done. A card that remounts mid-run shares the
// in-flight promise instead of re-charging; "done" persists on its tool-call entry in the thread.
const REDESIGN_ERROR_KEYS = {
  "scene-missing": "aideaChat.redesign.sceneMissing",
  "no-training": "aideaChat.redesign.noTraining",
  busy: "aideaChat.redesign.busy",
  unsupported: "aideaChat.redesign.unsupported",
  "insufficient-balance": "aideaChat.redesign.insufficientBalance",
  timeout: "aideaChat.redesign.timeout",
  "save-failed": "aideaChat.redesign.saveFailed",
  "too-large": "aideaChat.redesign.tooLarge",
};
const redesignsInFlight = new Map();
export function RedesignSceneState({ op, params, scope, cost, toolCallId }) {
  const store = useStore();
  const dispatch = useDispatch();
  const key = toolCallId || `${op}:${scope && scope.navId}:${params.format}:${params.variant}`;
  const { getToolState = () => null, setToolState = () => {} } = useChatCommit();
  // confirm | running | done | error | cancelled
  const [phase, setPhase] = useState(() =>
    getToolState(key) && getToolState(key).done
      ? "done"
      : redesignsInFlight.has(key)
        ? "running"
        : "confirm"
  );
  const [error, setError] = useState("");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const watch = useCallback((p) => {
    p.then((out) => {
      if (!mounted.current) return;
      if (out.error)
        setError(i18n.t(REDESIGN_ERROR_KEYS[out.error] || "aideaChat.redesign.failed"));
      setPhase(out.error ? "error" : "done");
    });
  }, []);
  useEffect(() => {
    if (redesignsInFlight.has(key)) watch(redesignsInFlight.get(key));
  }, [key]); // ponytail: mount-only; params/scope are new objects every render

  const onRedesign = useCallback(() => {
    setPhase("running");
    const p = runRedesignScene(dispatch, store.getState, params, scope)
      .catch(() => ({ error: "failed" }))
      .then((out) => {
        if (!out.error) setToolState(key, { done: true });
        return out;
      })
      .finally(() => redesignsInFlight.delete(key));
    redesignsInFlight.set(key, p);
    watch(p);
  }, [dispatch, store, params, scope, key, setToolState, watch]);

  const credits = cost && cost.credits;
  const line = (text) => (
    <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">{text}</p>
  );
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip op={op} />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
        <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
          {phase === "running" ? (
            <SpinnerIcon size={16} className="aidea-spin" />
          ) : phase === "error" ? (
            <AlertIcon size={16} />
          ) : phase === "done" ? (
            <CheckIcon size={16} />
          ) : (
            <ClapperIcon size={16} />
          )}
        </span>
        <div className="tw-min-w-0">
          {phase === "confirm" ? (
            <>
              {line(
                i18n.t("aideaChat.redesign.summary", {
                  format: params.format,
                  variant: params.variant,
                })
              )}
              {Number.isFinite(credits) ? (
                <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
                  {i18n.t("aideaChat.redesign.cost", { credits })}
                </p>
              ) : null}
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <ActionButton
                  label={i18n.t("aideaChat.redesign.run")}
                  onClick={onRedesign}
                  variant="primary"
                />
                <ActionButton
                  label={i18n.t("aideaChat.redesign.cancel")}
                  onClick={() => setPhase("cancelled")}
                />
              </div>
            </>
          ) : phase === "running" ? (
            line(i18n.t("aideaChat.redesign.running"))
          ) : phase === "done" ? (
            line(i18n.t("aideaChat.redesign.done"))
          ) : phase === "cancelled" ? (
            line(i18n.t("aideaChat.redesign.cancelled"))
          ) : (
            line(error)
          )}
        </div>
      </div>
    </Card>
  );
}

// Shared card body keyed by op + tier. Exported for the smoke test.
export function BulkMutateCard({ result, status, toolCallId, toolName }) {
  if (status && status.type === "running") {
    // The result has no op yet (nor does a precheck {error}); chip by tool name.
    return <RunningState op={toolName} />;
  }
  const parsed = parseMutationResult(result);
  if (!parsed) return <ErrorState op={toolName} />;
  if (parsed.error) return <ErrorState op={parsed.op || toolName} message={parsed.error} />;

  if (parsed.op === "motionDirectorScene") {
    return (
      <MotionDirectorState
        op={parsed.op}
        params={parsed.params}
        scope={parsed.scope}
        cost={parsed.cost}
        toolCallId={toolCallId}
      />
    );
  }
  if (parsed.op === "editMotionScene") {
    return (
      <MotionEditState
        op={parsed.op}
        params={parsed.params}
        scope={parsed.scope}
        summary={parsed.summary}
      />
    );
  }
  if (parsed.op === "redesignScene") {
    return (
      <RedesignSceneState
        op={parsed.op}
        params={parsed.params}
        scope={parsed.scope}
        cost={parsed.cost}
        toolCallId={toolCallId}
      />
    );
  }
  if (parsed.tier === "gated") {
    return (
      <GatedState
        op={parsed.op}
        params={parsed.params}
        scope={parsed.scope}
        cost={parsed.cost}
        summary={parsed.summary}
      />
    );
  }
  return (
    <InstantState
      op={parsed.op}
      params={parsed.params}
      scope={parsed.scope}
      summary={parsed.summary}
      toolCallId={toolCallId}
    />
  );
}

// Three registrations, one shared body. assistant-ui registers makeAssistantToolUI
// per tool name; rendering these once in ChatPanel hooks the renderer for each
// tool-call part while mounted under <AssistantRuntimeProvider>.
export const SetSceneFontToolUI = makeAssistantToolUI({
  toolName: "setSceneFontAll",
  render: (props) => <BulkMutateCard {...props} />,
});

export const SetNarrationVoiceToolUI = makeAssistantToolUI({
  toolName: "setNarrationVoiceAll",
  render: (props) => <BulkMutateCard {...props} />,
});

export const AnimateAllImagesToolUI = makeAssistantToolUI({
  toolName: "animateAllImagesInScene",
  render: (props) => <BulkMutateCard {...props} />,
});

export const MotionDirectorToolUI = makeAssistantToolUI({
  toolName: "motionDirectorScene",
  render: (props) => <BulkMutateCard {...props} />,
});

// Without this the agent calls editMotionScene, narrates that a card was
// shown, and NOTHING renders — the first live test went exactly that way: the
// assistant said the edit had started while no request had ever been made.
export const EditMotionSceneToolUI = makeAssistantToolUI({
  toolName: "editMotionScene",
  render: (props) => <BulkMutateCard {...props} />,
});

export const RedesignSceneToolUI = makeAssistantToolUI({
  toolName: "redesignScene",
  render: (props) => <BulkMutateCard {...props} />,
});
